// src/components/LearningFeedbackModal.jsx

import React, { useEffect, useId, useState } from "react";
import {
  ExternalLink,
  MessageSquareText,
  Star,
  X,
} from "lucide-react";

import {
  FEEDBACK_STATUSES,
  FEEDBACK_TYPES,
  GOOGLE_REVIEW_URL,
} from "../../lib/feedback/learningFeedback";

const DIFFICULTY_OPTIONS = [
  {
    value: 1,
    label: "Very easy",
  },
  {
    value: 2,
    label: "Easy",
  },
  {
    value: 3,
    label: "Moderate",
  },
  {
    value: 4,
    label: "Difficult",
  },
  {
    value: 5,
    label: "Very difficult",
  },
];

function FeedbackShell({
  title,
  message,
  icon: Icon,
  saving,
  error,
  onDismiss,
  children,
}) {
  const titleId = useId();
  const descriptionId = useId();

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/45 px-4 py-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
    >
      <div className="relative w-full max-w-[390px] rounded-[24px] bg-white px-6 py-7 shadow-2xl">
        <button
          type="button"
          onClick={onDismiss}
          disabled={saving}
          className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-[#F3F3F3] text-[#757575] transition hover:bg-[#E8E8E8] hover:text-black disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Close feedback"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#FFF1E8] text-[#FF914D]">
          <Icon className="h-8 w-8" />
        </div>

        <h2
          id={titleId}
          className="mt-4 text-center text-[24px] font-bold leading-tight text-[#333333]"
        >
          {title}
        </h2>

        <p
          id={descriptionId}
          className="mt-2 text-center text-[14px] leading-relaxed text-[#757575]"
        >
          {message}
        </p>

        {error && (
          <div
            className="mt-4 rounded-[12px] border border-[#FFB4B4] bg-[#FFF1F1] px-4 py-3 text-[13px] leading-relaxed text-[#B42318]"
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="mt-6">{children}</div>
      </div>
    </div>
  );
}

function DifficultyFeedback({
  event,
  saving,
  error,
  onSubmit,
  onDismiss,
}) {
  const [selectedRating, setSelectedRating] = useState(null);

  useEffect(() => {
    setSelectedRating(null);
  }, [event?.eventKey]);

  function handleSubmit() {
    if (!selectedRating || saving) return;

    onSubmit({
      status: FEEDBACK_STATUSES.SUBMITTED,
      difficultyRating: selectedRating,

      metadata: {
        response_action: "difficulty_rating",
      },
    });
  }

  return (
    <FeedbackShell
      title="How difficult was this level?"
      message={`What difficulty would you rate Level ${event.levelNumber}?`}
      icon={Star}
      saving={saving}
      error={error}
      onDismiss={onDismiss}
    >
      <div
        className="grid grid-cols-5 gap-2"
        role="radiogroup"
        aria-label="Level difficulty"
      >
        {DIFFICULTY_OPTIONS.map((option) => {
          const selected =
            selectedRating === option.value;

          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={`${option.value}: ${option.label}`}
              disabled={saving}
              onClick={() =>
                setSelectedRating(option.value)
              }
              className={[
                "flex h-12 items-center justify-center rounded-[12px] border-2 text-[18px] font-bold transition",
                selected
                  ? "border-[#FF914D] bg-[#FF914D] text-white"
                  : "border-[#E5E5E5] bg-white text-[#333333] hover:border-[#FF914D]",
                saving
                  ? "cursor-not-allowed opacity-60"
                  : "",
              ].join(" ")}
            >
              {option.value}
            </button>
          );
        })}
      </div>

      <div className="mt-3 flex justify-between text-[11px] font-semibold text-[#757575]">
        <span>1 = Very easy</span>
        <span>5 = Very difficult</span>
      </div>

      {selectedRating && (
        <p
          className="mt-3 text-center text-[13px] font-semibold text-[#333333]"
          aria-live="polite"
        >
          {DIFFICULTY_OPTIONS.find(
            (option) =>
              option.value === selectedRating
          )?.label}
        </p>
      )}

      <button
        type="button"
        onClick={handleSubmit}
        disabled={!selectedRating || saving}
        className="mt-5 flex h-[44px] w-full items-center justify-center rounded-[10px] bg-[#FF914D] px-4 text-[15px] font-bold text-white transition hover:bg-[#E9782F] disabled:cursor-not-allowed disabled:bg-[#D7D7D7]"
      >
        {saving ? "Saving..." : "SUBMIT RATING"}
      </button>

      <button
        type="button"
        onClick={onDismiss}
        disabled={saving}
        className="mt-3 flex h-[40px] w-full items-center justify-center rounded-[10px] text-[14px] font-semibold text-[#757575] underline disabled:cursor-not-allowed disabled:opacity-50"
      >
        Not now
      </button>
    </FeedbackShell>
  );
}

function QuestFeedback({
  event,
  saving,
  error,
  onSubmit,
  onDismiss,
}) {
  const [comment, setComment] = useState("");

  useEffect(() => {
    setComment("");
  }, [event?.eventKey]);

  const trimmedComment = comment.trim();

  function handleSubmitComment() {
    if (!trimmedComment || saving) return;

    onSubmit({
      status: FEEDBACK_STATUSES.SUBMITTED,
      comment: trimmedComment,

      metadata: {
        response_action: "written_feedback",
      },
    });
  }

  function handleEverythingGood() {
    if (saving) return;

    onSubmit({
      status: FEEDBACK_STATUSES.SUBMITTED,
      comment: null,

      metadata: {
        response_action: "everything_good",
      },
    });
  }

  return (
    <FeedbackShell
      title="Quest complete!"
      message="Well done on completing this Quest. Is there anything we can do to improve your learning experience?"
      icon={MessageSquareText}
      saving={saving}
      error={error}
      onDismiss={onDismiss}
    >
      <label
        htmlFor="quest-feedback-comment"
        className="block text-[13px] font-bold text-[#333333]"
      >
        Your feedback
      </label>

      <textarea
        id="quest-feedback-comment"
        value={comment}
        onChange={(eventObject) =>
          setComment(eventObject.target.value)
        }
        disabled={saving}
        maxLength={2000}
        rows={5}
        placeholder="Tell us what would make your learning experience better..."
        className="mt-2 w-full resize-none rounded-[12px] border border-[#D7D7D7] px-4 py-3 text-[14px] leading-relaxed text-[#333333] outline-none transition placeholder:text-[#A0A0A0] focus:border-[#FF914D] focus:ring-2 focus:ring-[#FFF1E8] disabled:cursor-not-allowed disabled:bg-[#F6F6F6]"
      />

      <div className="mt-1 text-right text-[11px] text-[#757575]">
        {comment.length}/2000
      </div>

      <button
        type="button"
        onClick={handleSubmitComment}
        disabled={!trimmedComment || saving}
        className="mt-4 flex h-[44px] w-full items-center justify-center rounded-[10px] bg-[#FF914D] px-4 text-[15px] font-bold text-white transition hover:bg-[#E9782F] disabled:cursor-not-allowed disabled:bg-[#D7D7D7]"
      >
        {saving ? "Saving..." : "SUBMIT FEEDBACK"}
      </button>

      <button
        type="button"
        onClick={handleEverythingGood}
        disabled={saving}
        className="mt-3 flex h-[44px] w-full items-center justify-center rounded-[10px] border-2 border-black bg-white px-4 text-[14px] font-bold text-black transition hover:bg-[#F3F3F3] disabled:cursor-not-allowed disabled:opacity-50"
      >
        EVERYTHING IS GOOD
      </button>

      <button
        type="button"
        onClick={onDismiss}
        disabled={saving}
        className="mt-3 flex h-[40px] w-full items-center justify-center rounded-[10px] text-[14px] font-semibold text-[#757575] underline disabled:cursor-not-allowed disabled:opacity-50"
      >
        Not now
      </button>
    </FeedbackShell>
  );
}

function GoogleReviewFeedback({
  saving,
  error,
  onSubmit,
  onDismiss,
}) {
  function handleGoogleReviewClick(event) {
    if (saving) {
      event.preventDefault();
      return;
    }

    /*
     * Do not prevent the default link action.
     *
     * The browser opens Google immediately in a new tab while
     * the parent records "opened_google" in Supabase.
     */
    onSubmit({
      status: FEEDBACK_STATUSES.OPENED_GOOGLE,

      metadata: {
        response_action: "opened_google_review",
      },
    });
  }

  return (
    <FeedbackShell
      title="You’ve completed Level 3!"
      message="We hope you’re enjoying Gwin. Would you be willing to leave us an honest Google review? Your feedback helps us improve and helps other entrepreneurs discover Gwin."
      icon={Star}
      saving={saving}
      error={error}
      onDismiss={onDismiss}
    >
      <a
        href={GOOGLE_REVIEW_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleGoogleReviewClick}
        aria-disabled={saving}
        className={[
          "flex h-[44px] w-full items-center justify-center gap-2 rounded-[10px] px-4 text-[15px] font-bold text-white transition",
          saving
            ? "cursor-not-allowed bg-[#D7D7D7]"
            : "bg-[#FF914D] hover:bg-[#E9782F]",
        ].join(" ")}
      >
        <ExternalLink className="h-4 w-4" />

        {saving
          ? "Saving..."
          : "LEAVE A GOOGLE REVIEW"}
      </a>

      <p className="mt-3 text-center text-[11px] leading-relaxed text-[#757575]">
        Google will open in a new tab. You will submit the
        review directly through Google.
      </p>

      <button
        type="button"
        onClick={onDismiss}
        disabled={saving}
        className="mt-3 flex h-[40px] w-full items-center justify-center rounded-[10px] text-[14px] font-semibold text-[#757575] underline disabled:cursor-not-allowed disabled:opacity-50"
      >
        Not now
      </button>
    </FeedbackShell>
  );
}

export function LearningFeedbackModal({
  event,
  saving = false,
  error = "",
  onSubmit,
  onDismiss,
}) {
  useEffect(() => {
    if (!event) return undefined;

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow =
        previousOverflow;
    };
  }, [event]);

  useEffect(() => {
    if (!event) return undefined;

    function handleKeyDown(keyboardEvent) {
      if (
        keyboardEvent.key === "Escape" &&
        !saving
      ) {
        onDismiss();
      }
    }

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [event, saving, onDismiss]);

  if (!event) return null;

  if (
    event.feedbackType ===
    FEEDBACK_TYPES.LEVEL_DIFFICULTY
  ) {
    return (
      <DifficultyFeedback
        event={event}
        saving={saving}
        error={error}
        onSubmit={onSubmit}
        onDismiss={onDismiss}
      />
    );
  }

  if (
    event.feedbackType ===
    FEEDBACK_TYPES.QUEST_FEEDBACK
  ) {
    return (
      <QuestFeedback
        event={event}
        saving={saving}
        error={error}
        onSubmit={onSubmit}
        onDismiss={onDismiss}
      />
    );
  }

  if (
    event.feedbackType ===
    FEEDBACK_TYPES.GOOGLE_REVIEW
  ) {
    return (
      <GoogleReviewFeedback
        saving={saving}
        error={error}
        onSubmit={onSubmit}
        onDismiss={onDismiss}
      />
    );
  }

  console.error(
    "Unsupported learning feedback event:",
    event
  );

  return null;
}

export default LearningFeedbackModal;